import { Button, Chip, Grid } from '@mui/material';
import { type ReactElement } from 'react';

import {
  type GetAvailableLocationsOutput,
  type GetAvailableRegionenOutput,
  type GetAvailableStandorteByRegionOutput,
  locationArtSchema,
} from '../../../../dtos';
import { locationArtLabels } from '../../../../utils/locationArtLabels';
import { sitzordnungLabelByValue } from '../../content';
import { type FilterParameters } from './ResourceCalendarFilters.utils';

type FilterChipsProps = {
  readonly filterParameters: FilterParameters;
  readonly onFilterChange: (filter: FilterParameters) => void;
  readonly deleteAllFilters: () => void;
  readonly availableRegions: GetAvailableRegionenOutput;
  readonly availableStandorte: GetAvailableStandorteByRegionOutput;
  readonly availableLocations: GetAvailableLocationsOutput;
  readonly lockedFilters?: Partial<Record<LockedFilterKey, boolean>>;
};

type LockedFilterKey = 'regionSapIds' | 'standortSapIds' | 'locationSapIds';

const isLockedFilterKey = (key: string): key is LockedFilterKey =>
  key === 'regionSapIds' || key === 'standortSapIds' || key === 'locationSapIds';

type FilterParametersKey = keyof FilterParameters;

type FilterLabelsTypeFieldsConfig = {
  field: FilterParametersKey;
  label: string;
  dataType: 'array' | 'number' | 'boolean';
};

const filterLabelsTypeFields: FilterLabelsTypeFieldsConfig[] = [
  { field: 'standortSapIds', label: 'Standort', dataType: 'array' },
  { field: 'locationSapIds', label: 'Location', dataType: 'array' },
  { field: 'raumgroesse', label: 'Raumgröße', dataType: 'number' },
  { field: 'regionSapIds', label: 'Region', dataType: 'array' },
  { field: 'locationArten', label: 'Art der Location', dataType: 'array' },
  { field: 'locationHatGruppenraum', label: 'Hat Gruppenraum', dataType: 'boolean' },
  { field: 'freieTage', label: 'Min. freie Tage', dataType: 'number' },
];

export const FilterChips = ({
  filterParameters,
  onFilterChange,
  deleteAllFilters,
  availableStandorte,
  availableRegions,
  availableLocations,
  lockedFilters,
}: FilterChipsProps) => {
  const hasCombinedRaumkapazitaetFilter =
    filterParameters.sitzordnung !== null && filterParameters.teilnehmerErwartet !== null;

  const getChipLabel = (config: FilterLabelsTypeFieldsConfig, filterData: string[] | number[]): string => {
    switch (config.field) {
      case 'standortSapIds':
        return `${config.label}: ${filterData.map((d) => availableStandorte.find((st) => st.standortSapId === d)?.name).join(', ')}`;
      case 'locationSapIds':
        return `${config.label}: ${filterData.map((d) => availableLocations.find((st) => st.locationSapId === d)?.kuerzel).join(', ')}`;
      case 'regionSapIds':
        return `${config.label}: ${filterData.map((d) => availableRegions.find((st) => st.regionSapId === d)?.name).join(', ')}`;
      case 'locationArten':
        return `${config.label}: ${filterData.map((d) => locationArtLabels[locationArtSchema.parse(d)]).join(', ')}`;
      default:
        return `${config.label}: ${filterData.join(', ')}`;
    }
  };

  const selectedFilterData = (config: FilterLabelsTypeFieldsConfig): string[] | number[] => {
    const filterParameter = filterParameters[config.field];
    if (filterParameter === null || (Array.isArray(filterParameter) && filterParameter.length === 0)) {
      return [];
    }

    if (typeof filterParameter === 'boolean') {
      return filterParameter ? ['Ja'] : ['Nein'];
    }

    if (Array.isArray(filterParameter)) {
      return filterParameter;
    }

    if (typeof filterParameter === 'number') {
      return [filterParameter];
    }

    return [];
  };

  const deleteFilter = (config: FilterLabelsTypeFieldsConfig): void => {
    if (config.dataType === 'boolean' || config.dataType === 'number') {
      onFilterChange({ ...filterParameters, [config.field]: null });
    }

    if (config.dataType === 'array') {
      onFilterChange({ ...filterParameters, [config.field]: [] });
    }
  };

  const renderChip = (field: keyof FilterParameters): ReactElement | null => {
    const config = filterLabelsTypeFields.find((e) => e.field === field);

    if (config) {
      const filterData = selectedFilterData(config);

      if (filterData.length > 0) {
        const isLocked = isLockedFilterKey(config.field) && lockedFilters?.[config.field] === true;
        return (
          <Chip
            label={getChipLabel(config, filterData)}
            variant="selection"
            color="secondary"
            disabled={isLocked}
            onDelete={isLocked ? undefined : () => deleteFilter(config)}
          />
        );
      }
    }

    return null;
  };

  const renderCombinedRaumkapazitaetChip = (): ReactElement | null => {
    if (!hasCombinedRaumkapazitaetFilter) {
      return null;
    }

    const { sitzordnung, teilnehmerErwartet } = filterParameters;

    const sitzordnungLabel =
      Object.entries(sitzordnungLabelByValue).find(([value]) => value === sitzordnung)?.[1] ?? sitzordnung;
    return (
      <Chip
        label={`Raumkapazität: ${sitzordnungLabel} mit ${teilnehmerErwartet} Pers.`}
        variant="selection"
        color="secondary"
        onDelete={() => onFilterChange({ ...filterParameters, sitzordnung: null, teilnehmerErwartet: null })}
      />
    );
  };

  const hasSelectedFilters = (): boolean => {
    if (hasCombinedRaumkapazitaetFilter) {
      return true;
    }

    for (const config of filterLabelsTypeFields) {
      if (config.field === 'sitzordnung' || config.field === 'teilnehmerErwartet') {
        continue;
      }

      const data = selectedFilterData(config);
      if (data.length > 0) return true;
    }

    return false;
  };

  if (!hasSelectedFilters()) {
    return null;
  }

  return (
    <Grid container spacing={1}>
      {renderChip('regionSapIds')}
      {renderChip('standortSapIds')}
      {renderChip('locationSapIds')}
      {renderChip('raumgroesse')}
      {renderCombinedRaumkapazitaetChip()}
      {renderChip('locationArten')}
      {renderChip('locationHatGruppenraum')}
      {renderChip('freieTage')}
      <Button onClick={deleteAllFilters} variant="text" size="small">
        Alle zurücksetzen
      </Button>
    </Grid>
  );
};
